import React, { useContext } from "react";
import { type, messageOfferStatus } from "venuescanner-js/constants/thread";
import { ViewContext } from "venuescanner-js/react/components/Inbox/context/ViewContext";
import userTypes from "venuescanner-js/constants/user_type";
import {
  ClosedViewedByOrganiser,
  ClosedViewedByHost,
} from "./MessageContent/Closed";
import { HostDeclined, OrganiserDeclined } from "./MessageContent/Declined";
import { Expired } from "./MessageContent/Expired";
import { HostNewEnquiry, OrganiserNewEnquiry } from "./MessageContent/New";
import { Booked } from "./MessageContent/Booked";
import {
  RequestToBookViewedByHost,
  RequestToBookViewedByOrganiser,
} from "./MessageContent/RequestToBook";
import {
  MarkedAsBookedViewedByHost,
  MarkedAsBookedViewedByOrganiser,
} from "./MessageContent/MarkedAsBooked";
import {
  AcceptedViewedByHost,
  AcceptedViewedByOrganiser,
} from "./MessageContent/Accepted";
import {
  ModifiedViewedByHost,
  ModifiedViewedByOrganiser,
} from "./MessageContent/Modified";
import {
  ReopenedViewedByHost,
  ReopenedViewedByOrganiser,
} from "./MessageContent/Reopened";
import { Payment } from "./MessageContent/Payment";

const MessageTypes = [
  "new",
  "modify",
  "expired",
  "accepted",
  "booked",
  "closed",
  "declined",
  "reopened",
  "call",
  "reopen",
  "payment",
  "payment_request",
  "marked_booked",
  "request_to_book",
].reduce((acc, curr) => {
  acc[curr.toUpperCase()] = curr.toLowerCase();
  return acc;
}, {});

const MessageType = ({
  type: messageType,
  offerStatus,
  previousOffer,
  viewerRole,
}) => {
  const previousOfferStatus = previousOffer ? previousOffer.status : null;
  const isChangeInOfferStatus = previousOfferStatus !== offerStatus;
  if (messageType === type.NEW) return MessageTypes.NEW;

  if (messageType === type.PAYMENT) return MessageTypes.PAYMENT;
  if (messageType === type.PAYMENT_REQUEST) return MessageTypes.PAYMENT_REQUEST;

  if (offerStatus === messageOfferStatus.ACCEPTED && isChangeInOfferStatus)
    return MessageTypes.ACCEPTED;
  if (messageType === type.SYSTEM && offerStatus === messageOfferStatus.EXPIRED)
    return MessageTypes.EXPIRED;

  if (
    ([messageOfferStatus.BOOKED, messageOfferStatus.CONFIRMED].includes(
      offerStatus
    ) &&
      isChangeInOfferStatus) ||
    (viewerRole === userTypes.host &&
      offerStatus === messageOfferStatus.MARK_BOOKED_HOST) ||
    (viewerRole === userTypes.organiser &&
      [
        messageOfferStatus.MARK_BOOKED_ORG,
        messageOfferStatus.MARK_BOOKED_HOST,
      ].includes(offerStatus))
  )
    return MessageTypes.BOOKED;

  if (messageType === type.BOOK_NUDGE_HOST) return MessageTypes.REQUEST_TO_BOOK;

  if (
    offerStatus === messageOfferStatus.MARK_BOOKED_ORG &&
    isChangeInOfferStatus &&
    viewerRole === userTypes.host
  )
    return MessageTypes.MARKED_BOOKED;

  if (offerStatus === messageOfferStatus.CANCELLED && isChangeInOfferStatus)
    return MessageTypes.CLOSED;

  if (
    offerStatus === messageOfferStatus.MARK_CLOSED_HOST &&
    isChangeInOfferStatus
  )
    return MessageTypes.CLOSED;

  if (messageType === type.CALL) return MessageTypes.CALL;

  if (messageType === type.REOPEN) return MessageTypes.REOPEN;

  if (offerStatus === messageOfferStatus.DECLINED && isChangeInOfferStatus)
    return MessageTypes.DECLINED;

  if (messageType === type.MODIFY) return MessageTypes.MODIFY;

  return null;
};

const contentByTypeAndViewer = {
  [MessageTypes.NEW]: {
    [userTypes.host]: HostNewEnquiry,
    [userTypes.organiser]: OrganiserNewEnquiry,
  },

  [MessageTypes.CLOSED]: {
    [userTypes.host]: ClosedViewedByHost,
    [userTypes.organiser]: ClosedViewedByOrganiser,
  },

  [MessageTypes.EXPIRED]: {
    [userTypes.host]: Expired,
    [userTypes.organiser]: Expired,
  },

  [MessageTypes.DECLINED]: {
    [userTypes.host]: HostDeclined,
    [userTypes.organiser]: OrganiserDeclined,
  },

  [MessageTypes.BOOKED]: {
    [userTypes.host]: Booked,
    [userTypes.organiser]: Booked,
  },
  [MessageTypes.REQUEST_TO_BOOK]: {
    [userTypes.host]: RequestToBookViewedByHost,
    [userTypes.organiser]: RequestToBookViewedByOrganiser,
  },

  [MessageTypes.MARKED_BOOKED]: {
    [userTypes.host]: MarkedAsBookedViewedByHost,
    [userTypes.organiser]: MarkedAsBookedViewedByOrganiser,
  },

  [MessageTypes.ACCEPTED]: {
    [userTypes.host]: AcceptedViewedByHost,
    [userTypes.organiser]: AcceptedViewedByOrganiser,
  },

  [MessageTypes.MODIFY]: {
    [userTypes.host]: ModifiedViewedByHost,
    [userTypes.organiser]: ModifiedViewedByOrganiser,
  },
  [MessageTypes.REOPEN]: {
    [userTypes.host]: ReopenedViewedByHost,
    [userTypes.organiser]: ReopenedViewedByOrganiser,
  },
  [MessageTypes.PAYMENT]: {
    [userTypes.host]: Payment,
    [userTypes.organiser]: Payment,
  },

  [MessageTypes.PAYMENT_REQUEST]: {
    [userTypes.host]: () => "",
    [userTypes.organiser]: () => "",
  },
};

const MessageContent = ({
  message,
  messageType,
  viewerRole,
  thread,
  cumulative,
}) => {
  const { activeUser, isMobileDevice, setActiveMobileView } =
    useContext(ViewContext);
  // console.log({ message, messageType, viewerRole })
  const MessageComponent =
    contentByTypeAndViewer[messageType] &&
    contentByTypeAndViewer[messageType][viewerRole];

  return MessageComponent ? (
    <MessageComponent
      message={message}
      messageType={messageType}
      viewerRole={viewerRole}
      activeUser={activeUser}
      isMobileDevice={isMobileDevice}
      cumulative={cumulative}
      thread={thread}
      setActiveMobileView={setActiveMobileView}
    />
  ) : null;
};

export { MessageTypes, MessageType, MessageContent };
